/**
 * Typed API wrappers for the MARS Live panel.
 *
 * All calls go through the shared `apiGet` so 401 redirects and error
 * envelopes are uniform. TanStack Query keys are colocated so
 * `prefetchQuery` stays in lockstep with `useQuery` in the components.
 */
import { apiGet } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import type { LiveFundId } from './store';
import type {
  ErResponse,
  ExposuresResponse,
  HorizonMonths,
  UiAsset,
  VariableName,
  VolatilityResponse,
  VintageMultiResponse,
  VintagesListResponse,
} from './types';

/** All variables rendered by the MARS Live fan grid (one fetch per vintage). */
export const FAN_VARIABLES: readonly VariableName[] = [
  'GDP',
  'Inflation',
  'T-Bill',
  'Dividend Yield',
  '10 Year',
  'BAA Spread',
];

export const YEARS_BACK = 3;

// TanStack Query keys

export const mlKeys = {
  all: ['mars-live'] as const,
  vintagesList: () => [...mlKeys.all, 'vintages', 'list'] as const,
  vintageMulti: (date: string, yearsBack: number = YEARS_BACK) =>
    [...mlKeys.all, 'vintages', date, 'multi', yearsBack] as const,
  expectedReturns: (asset: UiAsset, horizon: HorizonMonths) =>
    [...mlKeys.all, 'er', asset, horizon] as const,
  exposures: (
    fundId: string,
    volTiming: boolean,
    transactionCosts: boolean,
    constraints: boolean,
  ) => [...mlKeys.all, 'exposures', fundId, volTiming, transactionCosts, constraints] as const,
  volatility: () => [...mlKeys.all, 'volatility'] as const,
};

// Fetch functions

export async function fetchVintageList(): Promise<string[]> {
  const res = await apiGet<VintagesListResponse>(endpoints.mars.fanVintageList());
  return res.vintages;
}

export async function fetchVintageMulti(
  vintageDate: string,
  yearsBack: number = YEARS_BACK,
): Promise<VintageMultiResponse['data']> {
  const res = await apiGet<VintageMultiResponse>(
    endpoints.mars.fanVintageMulti(vintageDate, {
      variables: FAN_VARIABLES,
      years_back: yearsBack,
    }),
  );
  return res.data;
}

export async function fetchExpectedReturns(
  asset: UiAsset,
  horizonMonths: HorizonMonths,
  returnType: 'excess' | 'nominal' | 'real' = 'excess',
): Promise<ErResponse['data']> {
  const res = await apiGet<ErResponse>(
    endpoints.mars.liveExpectedReturns({
      asset_class: asset,
      horizon_months: horizonMonths,
      return_type: returnType,
    }),
  );
  return res.success ? res.data : [];
}

export async function fetchExposures(
  fundId: LiveFundId,
  volTiming: boolean,
  transactionCosts: boolean,
  constraints: boolean,
): Promise<ExposuresResponse['data']> {
  const res = await apiGet<ExposuresResponse>(
    endpoints.mars.portfolioExposures(fundId, {
      timeframe: '20y',
      vol_timing: volTiming,
      tc: transactionCosts,
      constraints,
    }),
  );
  return res.success ? res.data : null;
}

export async function fetchVolatility(): Promise<VolatilityResponse | null> {
  // Market-level FQM vol surface, login-only on the backend — NOT the
  // fund-gated portfolio timeseries, so it works for every persona
  // (fulcrum PMs, fidelity, gic) on MARS Live, Forecasts and chat.
  const res = await apiGet<{ success: boolean; data?: VolatilityResponse }>(
    endpoints.mars.liveAssetClassVol(),
  );
  return res.success && res.data ? res.data : null;
}

// Helpers

/**
 * Find the most recent vintage at least 30 days before `date`, used by the
 * fan plot to overlay the "One Month Ago" forecast.
 */
export function findPrevMonth(vintages: string[], date: string): string | null {
  const cutoff = new Date(date);
  cutoff.setDate(cutoff.getDate() - 30);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  let prev: string | null = null;
  for (const v of vintages) {
    if (v <= cutoffStr) prev = v;
    else break;
  }
  return prev;
}
